<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const activeTab = 'organisation'
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-4xl mx-auto px-5">

        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <div class="flex items-start justify-between">
              <div class="flex items-center space-x-4">
                <div class="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <i class="fas fa-building text-primary text-2xl"></i>
                </div>
                <div>
                  <h1 class="text-2xl font-bold text-accent">GreenPath Carbon Pty Ltd</h1>
                  <p class="text-gray-500 text-sm">Developer ID: DIN-2024-001247 &nbsp;·&nbsp; Registered: 15 Jan 2024</p>
                  <span class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">Active</span>
                </div>
              </div>
              <button class="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90">
                <i class="fas fa-save mr-2"></i>Save Changes
              </button>
            </div>
          </div>
        </section>

        <!-- Tabs -->
        <div class="flex space-x-1 border-b border-gray-200 mb-6">
          <button class="px-4 py-2 text-sm font-medium text-primary border-b-2 border-primary">Organisation</button>
          <button class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Contact</button>
          <button class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Security</button>
          <button class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Notifications</button>
        </div>

        <div class="space-y-6">
          <!-- Organisation info -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-accent mb-4">Organisation Information</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Organisation Name</label>
                <input type="text" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" value="GreenPath Carbon Pty Ltd" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Legal Status</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option selected>Private Limited Company</option>
                  <option>Public Limited Company</option>
                  <option>NGO / Non-profit</option>
                  <option>Government Entity</option>
                  <option>Individual</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Business Registration Number</label>
                <input type="text" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" value="ABN 45 678 901 234" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Country of Incorporation</label>
                <select class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                  <option selected>Australia</option>
                </select>
              </div>
            </div>
          </section>

          <!-- Contact info -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-accent mb-4">Primary Contact</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input type="text" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" value="Jane Smith" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Job Title</label>
                <input type="text" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" value="Carbon Projects Manager" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input type="email" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" value="j.smith@greenpath.com.au" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input type="tel" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" value="+61 2 9999 0001" />
              </div>
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">Physical Address</label>
                <textarea rows="2" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">Level 5, 123 Carbon Street, Sydney NSW 2000, Australia</textarea>
              </div>
            </div>
          </section>

          <!-- Account details (readonly) -->
          <section class="bg-white rounded-lg border border-gray-200 p-6">
            <h2 class="text-lg font-semibold text-accent mb-4">Account Details</h2>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
              <div><p class="text-gray-500">Developer ID</p><p class="font-medium text-gray-800">DIN-2024-001247</p></div>
              <div><p class="text-gray-500">Account Type</p><p class="font-medium text-gray-800">Project Developer</p></div>
              <div><p class="text-gray-500">Registration Date</p><p class="font-medium text-gray-800">15 January 2024</p></div>
              <div><p class="text-gray-500">Account Status</p><p class="font-medium text-green-600">Active</p></div>
              <div><p class="text-gray-500">Preferred Language</p>
                <select class="mt-1 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option>English</option>
                  <option>French</option>
                  <option>Swahili</option>
                </select>
              </div>
            </div>
          </section>
        </div>

      </div>
    </main>
  </div>
</template>
